/**
 * Copyright (c) 2018-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Loci, isEmptyLoci, isEveryLoci, EveryLoci } from '../mol-model/loci';
import { applyMarkerAction, getMarkerInfo, setMarkerValue, getPartialMarkerAverage, MarkerActions } from '../mol-util/marker-action';
import { Mat4 } from '../mol-math/linear-algebra';
import { updateTransformData, fillIdentityTransform } from '../mol-geo/geometry/transform-data';
import { calculateTransformBoundingSphere } from '../mol-gl/renderable/util';
import { ValueCell } from '../mol-util';
import { createOverpaint, clearOverpaint, applyOverpaintColor } from '../mol-geo/geometry/overpaint-data';
import { Interval } from '../mol-data/int';
import { createTransparency, clearTransparency, applyTransparencyValue, getTransparencyAverage } from '../mol-geo/geometry/transparency-data';
import { createClipping, applyClippingGroups, clearClipping } from '../mol-geo/geometry/clipping-data';
import { getMarkersAverage } from '../mol-geo/geometry/marker-data';
export { Visual };
var Visual;
(function (Visual) {
    function setVisibility(renderObject, visible) {
        if (renderObject)
            renderObject.state.visible = visible;
    }
    Visual.setVisibility = setVisibility;
    function setAlphaFactor(renderObject, alphaFactor) {
        if (renderObject)
            renderObject.state.alphaFactor = alphaFactor;
    }
    Visual.setAlphaFactor = setAlphaFactor;
    function setPickable(renderObject, pickable) {
        if (renderObject)
            renderObject.state.pickable = pickable;
    }
    Visual.setPickable = setPickable;
    function setColorOnly(renderObject, colorOnly) {
        if (renderObject)
            renderObject.state.colorOnly = colorOnly;
    }
    Visual.setColorOnly = setColorOnly;
    function mark(renderObject, loci, action, lociApply, previous) {
        if (!renderObject || isEmptyLoci(loci))
            return false;
        var _a = renderObject.values, tMarker = _a.tMarker, dMarkerType = _a.dMarkerType, uMarker = _a.uMarker, markerAverage = _a.markerAverage, markerStatus = _a.markerStatus, uGroupCount = _a.uGroupCount, instanceCount = _a.instanceCount;
        var count = uGroupCount.ref.value * instanceCount.ref.value;
        var array = tMarker.ref.value.array;
        var currentStatus = markerStatus.ref.value;
        if (!isEveryLoci(loci)) {
            var intervalSize_1 = 0;
            lociApply(loci, function (interval) {
                intervalSize_1 += Interval.size(interval);
                return true;
            }, true);
            if (intervalSize_1 === 0)
                return false;
            if (intervalSize_1 === count)
                loci = EveryLoci;
        }
        var changed = false;
        var average = -1;
        var status = -1;
        if (isEveryLoci(loci)) {
            var info = getMarkerInfo(action, currentStatus);
            if (info.status !== -1) {
                changed = currentStatus !== info.status;
                if (changed)
                    setMarkerValue(array, info.status, count);
            }
            else {
                changed = applyMarkerAction(array, Interval.ofLength(count), action);
            }
            average = info.average;
            status = info.status;
        }
        else {
            changed = lociApply(loci, function (interval) { return applyMarkerAction(array, interval, action); }, true);
            if (changed) {
                average = getPartialMarkerAverage(action, currentStatus);
                if (previous && previous.status !== -1 && average === -1 &&
                    MarkerActions.isReverse(previous.action, action) &&
                    Loci.areEqual(loci, previous.loci)) {
                    status = previous.status;
                    average = status === 0 ? 0 : 0.5;
                }
            }
        }
        if (changed) {
            if (average === -1) {
                average = getMarkersAverage(array, count);
                if (average === 0)
                    status = 0;
            }
            if (previous) {
                previous.action = action;
                previous.loci = loci;
                previous.status = currentStatus;
            }
            ValueCell.updateIfChanged(uMarker, status);
            if (status === -1)
                ValueCell.update(tMarker, tMarker.ref.value);
            ValueCell.updateIfChanged(dMarkerType, status === -1 ? 'groupInstance' : 'uniform');
            ValueCell.updateIfChanged(markerAverage, average);
            ValueCell.updateIfChanged(markerStatus, status);
        }
        return changed;
    }
    Visual.mark = mark;
    function setOverpaint(renderObject, overpaint, lociApply, clear) {
        if (!renderObject)
            return;
        var _a = renderObject.values, tOverpaint = _a.tOverpaint, uGroupCount = _a.uGroupCount, instanceCount = _a.instanceCount;
        var count = uGroupCount.ref.value * instanceCount.ref.value;
        // ensure texture has right size
        createOverpaint(overpaint.layers.length ? count : 0, renderObject.values);
        var array = tOverpaint.ref.value.array;
        // clear all if requested
        if (clear)
            clearOverpaint(array, 0, count);
        var _loop_1 = function (i, il) {
            var _a = overpaint.layers[i], loci = _a.loci, color = _a.color, clear_1 = _a.clear;
            var apply = function (interval) {
                var start = Interval.start(interval);
                var end = Interval.end(interval);
                return clear_1
                    ? clearOverpaint(array, start, end)
                    : applyOverpaintColor(array, start, end, color);
            };
            lociApply(loci, apply, false);
        };
        for (var i = 0, il = overpaint.layers.length; i < il; ++i) {
            _loop_1(i, il);
        }
        ValueCell.update(tOverpaint, tOverpaint.ref.value);
    }
    Visual.setOverpaint = setOverpaint;
    function setTransparency(renderObject, transparency, lociApply, clear) {
        if (!renderObject)
            return;
        var _a = renderObject.values, tTransparency = _a.tTransparency, transparencyAverage = _a.transparencyAverage, uGroupCount = _a.uGroupCount, instanceCount = _a.instanceCount;
        var count = uGroupCount.ref.value * instanceCount.ref.value;
        // ensure texture has right size and variant
        createTransparency(transparency.layers.length ? count : 0, renderObject.values);
        var array = tTransparency.ref.value.array;
        // clear if requested
        if (clear)
            clearTransparency(array, 0, count);
        var _loop_2 = function (i, il) {
            var _a = transparency.layers[i], loci = _a.loci, value = _a.value;
            var apply = function (interval) {
                var start = Interval.start(interval);
                var end = Interval.end(interval);
                return applyTransparencyValue(array, start, end, value);
            };
            lociApply(loci, apply, false);
        };
        for (var i = 0, il = transparency.layers.length; i < il; ++i) {
            _loop_2(i, il);
        }
        ValueCell.update(tTransparency, tTransparency.ref.value);
        ValueCell.updateIfChanged(transparencyAverage, getTransparencyAverage(array, count));
    }
    Visual.setTransparency = setTransparency;
    function setClipping(renderObject, clipping, lociApply, clear) {
        if (!renderObject)
            return;
        var _a = renderObject.values, tClipping = _a.tClipping, uGroupCount = _a.uGroupCount, instanceCount = _a.instanceCount;
        var count = uGroupCount.ref.value * instanceCount.ref.value;
        // ensure texture has right size
        createClipping(clipping.layers.length ? count : 0, renderObject.values);
        var array = tClipping.ref.value.array;
        // clear if requested
        if (clear)
            clearClipping(array, 0, count);
        var _loop_3 = function (i, il) {
            var _a = clipping.layers[i], loci = _a.loci, groups = _a.groups;
            var apply = function (interval) {
                var start = Interval.start(interval);
                var end = Interval.end(interval);
                return applyClippingGroups(array, start, end, groups);
            };
            lociApply(loci, apply, false);
        };
        for (var i = 0, il = clipping.layers.length; i < il; ++i) {
            _loop_3(i, il);
        }
        ValueCell.update(tClipping, tClipping.ref.value);
    }
    Visual.setClipping = setClipping;
    function setTransform(renderObject, transform, instanceTransforms) {
        if (!renderObject || (!transform && !instanceTransforms))
            return;
        var values = renderObject.values;
        if (transform) {
            Mat4.copy(values.matrix.ref.value, transform);
            ValueCell.update(values.matrix, values.matrix.ref.value);
        }
        if (instanceTransforms) {
            values.extraTransform.ref.value.set(instanceTransforms);
            ValueCell.update(values.extraTransform, values.extraTransform.ref.value);
        }
        else if (instanceTransforms === null) {
            fillIdentityTransform(values.extraTransform.ref.value, values.instanceCount.ref.value);
            ValueCell.update(values.extraTransform, values.extraTransform.ref.value);
        }
        updateTransformData(values);
        var boundingSphere = calculateTransformBoundingSphere(values.invariantBoundingSphere.ref.value, values.aTransform.ref.value, values.instanceCount.ref.value);
        ValueCell.update(values.boundingSphere, boundingSphere);
    }
    Visual.setTransform = setTransform;
})(Visual || (Visual = {}));
//# sourceMappingURL=visual.js.map